import * as React from 'react';
import { Autocomplete } from '@base-ui/react/autocomplete';

import { cn } from '@ui/lib/utils';

/** The open-list half of `ComboboxField`; portalled, since the fields that want it sit in clipped boxes. */
export type AutocompleteFieldProps = {
  id?: string;
  'aria-label'?: string;
  /** The usual answers, offered in the order given. */
  suggestions: readonly string[];
  value: string;
  onChange: (next: string) => void;
  /** Called once focus has left for good, popup included: pressing an option is not someone leaving. */
  onBlur?: () => void;
  placeholder?: string;
  /** Whether the list is open as the field arrives, for a field that appears on a click of its own. */
  defaultOpen?: boolean;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
};

export function AutocompleteField({
  id,
  'aria-label': ariaLabel,
  suggestions,
  value,
  onChange,
  onBlur,
  placeholder,
  defaultOpen = false,
  autoFocus = false,
  className,
  disabled,
}: AutocompleteFieldProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const popupRef = React.useRef<HTMLDivElement | null>(null);
  const listOpen = React.useRef(defaultOpen && suggestions.length > 0);

  /** Asked once focus has settled, never while the list is open: a pointer pick blurs on the way down. */
  const leaveIfFocusGone = (): void => {
    if (!onBlur) return;
    window.setTimeout(() => {
      if (listOpen.current) return;
      const focused = document.activeElement;
      if (focused === inputRef.current) return;
      if (popupRef.current && focused && popupRef.current.contains(focused)) return;
      onBlur();
    }, 0);
  };

  return (
    <Autocomplete.Root
      items={suggestions as string[]}
      value={value}
      // Escape on a closed list empties the field, which is wrong where the text is the answer.
      onValueChange={(next, details) => {
        if (details?.reason === 'escape-key') return;
        onChange(next);
      }}
      defaultOpen={defaultOpen && suggestions.length > 0}
      openOnInputClick
      disabled={disabled}
      // Clicking away closes the list before the input blurs, so that blur never arrives.
      onOpenChange={open => {
        listOpen.current = open;
        if (!open) leaveIfFocusGone();
      }}
    >
      <Autocomplete.Input
        ref={inputRef}
        id={id}
        aria-label={ariaLabel}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onBlur={leaveIfFocusGone}
        className={cn(
          'h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-base outline-none transition-colors',
          'placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30',
          className,
        )}
      />
      <Autocomplete.Portal>
        {/* Below the field, left-aligned, flipping above only where there is no room below. */}
        <Autocomplete.Positioner side="bottom" align="start" sideOffset={4} className="z-50">
          <Autocomplete.Popup
            ref={popupRef}
            data-slot="autocomplete-content"
            className={cn(
              'w-[max(12rem,var(--anchor-width))] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
              'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
              // An empty box reads as a row of the table it hangs over.
              'data-[empty]:hidden',
            )}
          >
            <Autocomplete.List className="max-h-60 overflow-y-auto p-1">
              {(item: string) => (
                <Autocomplete.Item
                  key={item}
                  value={item}
                  className={cn(
                    'cursor-pointer rounded-md px-2 py-1 text-sm outline-none',
                    'data-[highlighted]:bg-muted/60',
                  )}
                >
                  {item}
                </Autocomplete.Item>
              )}
            </Autocomplete.List>
          </Autocomplete.Popup>
        </Autocomplete.Positioner>
      </Autocomplete.Portal>
    </Autocomplete.Root>
  );
}
